import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, formatDateTime, formatCZK } from "@/components/Shared";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Download, Users, Activity, ChevronDown, ChevronRight, Pencil, Save, X as XIcon, AlertTriangle, Trash2, CalendarPlus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import AbsenceDialog, { AbsenceList } from "@/components/AbsenceDialog";

const MONTHS_CS = [
  "Leden", "Únor", "Březen", "Duben", "Květen", "Červen",
  "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec",
];

function formatHM(minutes) {
  if (minutes === undefined || minutes === null) return "0:00";
  const m = Math.round(Number(minutes) || 0);
  const sign = m < 0 ? "-" : "";
  const abs = Math.abs(m);
  return `${sign}${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, "0")}`;
}

function hoursToMinutes(h) {
  return Math.round(Number(h || 0) * 60);
}

// datetime-local input <-> ISO util
function toLocalInputValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalInputValue(v) {
  if (!v) return null;
  return new Date(v).toISOString();
}

export default function AttendanceAdmin() {
  const { user } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [branchId, setBranchId] = useState(user.role === "manager" ? user.branch_id : "");
  const [branches, setBranches] = useState([]);
  const [active, setActive] = useState([]);
  const [summary, setSummary] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [shiftsByUser, setShiftsByUser] = useState({});
  const [editing, setEditing] = useState(null); // { shift, start, end, breaks, note }
  const [autoStats, setAutoStats] = useState(null);
  const [absences, setAbsences] = useState([]);
  const [users, setUsers] = useState([]);
  const [absenceOpen, setAbsenceOpen] = useState(false);

  const load = async () => {
    // měsíční rozsah pro absence
    const lastDay = new Date(year, month, 0).getDate();
    const fromDate = `${year}-${String(month).padStart(2, "0")}-01`;
    const toDate = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    const calls = [
      api.get("/branches"),
      api.get("/attendance/active"),
      api.get("/attendance/summary", { params: { year, month, branch_id: branchId || undefined } }),
      api.get("/attendance/auto-closed-stats", { params: { days: 7 } }),
      api.get("/attendance/absences", { params: { from_date: fromDate, to_date: toDate, branch_id: branchId || undefined } }),
    ];
    if (user.role === "admin") {
      calls.push(api.get("/users"));
    }
    const results = await Promise.all(calls);
    setBranches(results[0].data);
    setActive(results[1].data);
    setSummary(results[2].data);
    setAutoStats(results[3].data);
    setAbsences(results[4].data);
    if (results[5]) setUsers(results[5].data);
    setExpanded({});
    setShiftsByUser({});
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month, branchId]);

  const brName = (id) => branches.find((x) => x.id === id)?.name || "—";

  const totalActive = active.length;
  const totalHoursThisMonth = useMemo(
    () => (summary?.items || []).reduce((s, x) => s + Number(x.total_hours || 0), 0),
    [summary]
  );

  const toggleUser = async (uid) => {
    const opening = !expanded[uid];
    setExpanded((e) => ({ ...e, [uid]: opening }));
    if (opening && !shiftsByUser[uid]) {
      try {
        const { data } = await api.get(`/attendance/user/${uid}`, {
          params: { year, month },
        });
        setShiftsByUser((m) => ({ ...m, [uid]: data }));
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba načtení");
      }
    }
  };

  const downloadCsv = async () => {
    try {
      const params = new URLSearchParams({ year: String(year), month: String(month) });
      if (branchId) params.set("branch_id", branchId);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/attendance/export.csv?${params}`;
      const token = localStorage.getItem("cafecko_token");
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("HTTP " + resp.status);
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `dochazka-${year}-${String(month).padStart(2, "0")}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast.error("Export selhal");
    }
  };

  const openEdit = (s) => {
    setEditing({
      shift: s,
      start: toLocalInputValue(s.start_at),
      end: toLocalInputValue(s.end_at),
      breaks: (s.breaks || []).map((b) => ({
        start_at: toLocalInputValue(b.start_at),
        end_at: toLocalInputValue(b.end_at),
      })),
      note: s.correction_note || "",
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      const body = {
        start_at: fromLocalInputValue(editing.start),
        end_at: editing.end ? fromLocalInputValue(editing.end) : "",
        breaks: editing.breaks.map((b) => ({
          start_at: fromLocalInputValue(b.start_at),
          end_at: b.end_at ? fromLocalInputValue(b.end_at) : null,
        })),
        correction_note: editing.note,
      };
      const { data } = await api.patch(`/attendance/${editing.shift.id}`, body);
      // refresh in-cache
      const uid = editing.shift.user_id;
      setShiftsByUser((m) => ({
        ...m,
        [uid]: (m[uid] || []).map((x) => (x.id === data.id ? data : x)),
      }));
      toast.success("Směna upravena");
      setEditing(null);
      // refresh summary totals
      const s = await api.get("/attendance/summary", { params: { year, month, branch_id: branchId || undefined } });
      setSummary(s.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
    }
  };

  const deleteShift = async (s) => {
    if (!confirm("Smazat směnu? Této akci nelze vrátit zpět.")) return;
    try {
      await api.delete(`/attendance/${s.id}`);
      setShiftsByUser((m) => ({
        ...m,
        [s.user_id]: (m[s.user_id] || []).filter((x) => x.id !== s.id),
      }));
      const sum = await api.get("/attendance/summary", { params: { year, month, branch_id: branchId || undefined } });
      setSummary(sum.data);
      toast.success("Smazáno");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
    }
  };

  return (
    <div data-testid="attendance-admin-page">
      <PageHeader
        eyebrow="Personalistika"
        title="Docházky"
        description="Kdo je aktuálně v práci, měsíční přehled hodin a export pro účetní."
      />

      {autoStats && autoStats.shifts_count > 0 && (
        <div
          data-testid="auto-closed-banner"
          className="rounded-lg border border-[#C29329] mb-6 p-4 sm:p-5"
          style={{ background: "#C293291A" }}
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-[#8A6A1F] shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-[#8A6A1F] mb-1">
                {autoStats.user_count === 1 ? (
                  <>1 zaměstnanec zapomněl odepsat odchod (posledních 7 dní)</>
                ) : (
                  <>{autoStats.user_count} zaměstnanců zapomnělo odepsat odchod (posledních 7 dní)</>
                )}
              </div>
              <div className="text-xs text-[#57534E] mb-2">
                Systém automaticky uzavřel <strong>{autoStats.shifts_count}</strong>{" "}
                {autoStats.shifts_count === 1 ? "směnu" : autoStats.shifts_count < 5 ? "směny" : "směn"}{" "}
                15 hodin po nástupu. Zkontrolujte správnost hodin a případně upravte.
              </div>
              <ul className="flex flex-wrap gap-1.5 mt-2">
                {autoStats.users.map((u) => (
                  <li
                    key={u.user_id}
                    className="text-xs px-2 py-1 rounded-full bg-white border border-[#C2932966]"
                  >
                    <span className="font-medium">{u.name}</span>
                    {u.count > 1 && (
                      <span className="ml-1 text-[#8A6A1F]">×{u.count}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Active now */}
      <div className="card-cafe p-4 sm:p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-4 h-4 text-[#376A4D]" />
          <div className="label-eyebrow">Právě v práci</div>
          <span className="text-xs text-[#57534E]">({totalActive})</span>
        </div>
        {active.length === 0 ? (
          <div className="text-sm text-[#57534E] py-6 text-center border border-dashed border-[#E5E1D8] rounded-md">
            Nikdo nemá aktivní směnu.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="active-shifts">
            {active.map((s) => (
              <div key={s.id} className="border border-[#E5E1D8] rounded-md p-3 flex items-center gap-3 bg-white">
                {s.start_selfie ? (
                  <img src={s.start_selfie} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-[#376A4D]" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#F9F6F0] grid place-items-center border-2 border-[#E5E1D8]">
                    <Users className="w-5 h-5 text-[#57534E]" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{s.user_name || s.user_id?.slice(0, 8)}</div>
                  <div className="text-xs text-[#57534E] truncate">{brName(s.branch_id)}</div>
                  <div className="text-xs text-[#57534E]">od {formatDateTime(s.start_at)}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="mono font-semibold text-[#376A4D]">{formatHM(s.total_minutes)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="card-cafe p-4 mb-6 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-[#57534E]">Měsíc:</span>
          <select
            data-testid="att-month"
            className="input-cafe py-1.5"
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
          >
            {MONTHS_CS.map((n, i) => <option key={i + 1} value={i + 1}>{n}</option>)}
          </select>
          <input
            data-testid="att-year"
            type="number"
            inputMode="numeric"
            pattern="[0-9]*"
            className="input-cafe py-1.5 w-24"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          />
        </div>
        {user.role === "admin" && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-[#57534E]">Pobočka:</span>
            <select
              data-testid="att-branch"
              className="input-cafe py-1.5"
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
            >
              <option value="">— všechny —</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        )}
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <span className="text-xs text-[#57534E]">Pracovních dní: <strong>{summary?.working_days ?? "—"}</strong></span>
          <button
            data-testid="att-add-absence"
            onClick={() => setAbsenceOpen(true)}
            className="px-3 py-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0] inline-flex items-center gap-1.5 text-sm"
          >
            <CalendarPlus className="w-4 h-4" /> Nepřítomnost
          </button>
          <button
            data-testid="att-export-csv"
            onClick={downloadCsv}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* Absence — přehled všech nepřítomností v měsíci */}
      {absences.length > 0 && (
        <div className="card-cafe p-5 sm:p-6 mb-6" data-testid="attadmin-absences-panel">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="label-eyebrow">Nepřítomnosti</div>
              <h3 className="text-lg font-semibold" style={{ fontFamily: "Outfit" }}>
                {MONTHS_CS[month - 1]} {year} · {absences.length}{" "}
                {absences.length === 1 ? "záznam" : absences.length < 5 ? "záznamy" : "záznamů"}
              </h3>
            </div>
            <div className="text-xs text-[#57534E]">
              Dovolená, lékař, nemoc — nezapočítává se do odpracovaných hodin
            </div>
          </div>
          <AbsenceList
            items={absences}
            showUser
            onDelete={async (a) => {
              try {
                await api.delete(`/attendance/absences/${a.id}`);
                toast.success("Záznam smazán");
                await load();
              } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
              }
            }}
          />
        </div>
      )}

      {/* Summary table */}
      <div className="card-cafe overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-[#E5E1D8] flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="label-eyebrow">Měsíční přehled</div>
            <h2 className="text-lg sm:text-2xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>
              {MONTHS_CS[month - 1]} {year}
            </h2>
          </div>
          <div className="text-sm text-[#57534E]">
            Celkem za měsíc: <strong className="mono">{formatHM(totalHoursThisMonth * 60)}</strong>
          </div>
        </div>

        {(summary?.items || []).length === 0 ? (
          <div className="p-10 text-center text-[#57534E] text-sm">Žádná docházka v tomto měsíci.</div>
        ) : (
          <ul className="divide-y divide-[#E5E1D8]" data-testid="att-summary-list">
            {summary.items.map((it) => {
              const isOpen = expanded[it.user_id];
              const userShifts = shiftsByUser[it.user_id] || [];
              return (
                <li key={it.user_id} className="bg-white">
                  <button
                    data-testid={`att-row-${it.user_id}`}
                    onClick={() => toggleUser(it.user_id)}
                    className="w-full flex items-center gap-3 px-4 sm:px-6 py-3 text-left hover:bg-[#F9F6F0] transition-colors"
                  >
                    {isOpen ? <ChevronDown className="w-4 h-4 shrink-0 text-[#57534E]" /> : <ChevronRight className="w-4 h-4 shrink-0 text-[#57534E]" />}
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{it.name}</div>
                      <div className="text-xs text-[#57534E] truncate">{brName(it.branch_id)} · {it.shifts_count} směn{it.shifts_count === 1 ? "a" : it.shifts_count < 5 ? "y" : ""}</div>
                    </div>
                    <div className="hidden sm:grid grid-cols-5 gap-3 text-sm text-right">
                      <Cell label="Fond" value={formatHM(it.expected_hours * 60)} />
                      <Cell label="Odpracováno" value={formatHM(it.total_hours * 60)} accent="#376A4D" strong />
                      <Cell label="Víkend" value={formatHM(it.weekend_hours * 60)} accent={it.weekend_hours ? "#C29329" : "#57534E"} />
                      <Cell
                        label="Absence"
                        value={it.absence_hours > 0 ? formatHM(it.absence_hours * 60) : "—"}
                        accent={it.absence_hours > 0 ? "#3D6FB8" : "#57534E"}
                        title={
                          it.absence_hours > 0
                            ? `Dovolená ${it.vacation_hours.toFixed(1)} h · Lékař ${it.doctor_hours.toFixed(1)} h · Nemoc ${it.sick_hours.toFixed(1)} h · Jiné ${it.other_absence_hours.toFixed(1)} h`
                            : "Bez nepřítomností"
                        }
                      />
                      <Cell label="Rozdíl"
                            value={`${it.diff_hours >= 0 ? "+" : ""}${formatHM(it.diff_hours * 60)}`}
                            accent={it.diff_hours >= 0 ? "#376A4D" : "#9E4747"} />
                    </div>
                    <div className="sm:hidden text-right shrink-0">
                      <div className="mono font-semibold">{formatHM(it.total_hours * 60)}</div>
                      <div className="text-xs text-[#57534E]">fond {formatHM(it.expected_hours * 60)}</div>
                    </div>
                  </button>

                  {/* Mobile detail cells */}
                  <div className="sm:hidden grid grid-cols-4 gap-2 px-4 pb-3 text-xs">
                    <MobileCell label="Víkend" value={formatHM(it.weekend_hours * 60)} accent={it.weekend_hours ? "#C29329" : undefined} />
                    <MobileCell label="Svátky" value={formatHM(it.holiday_hours * 60)} accent={it.holiday_hours ? "#9E4747" : undefined} />
                    <MobileCell
                      label="Absence"
                      value={it.absence_hours > 0 ? formatHM(it.absence_hours * 60) : "—"}
                      accent={it.absence_hours > 0 ? "#3D6FB8" : undefined}
                    />
                    <MobileCell label="Rozdíl"
                                value={`${it.diff_hours >= 0 ? "+" : ""}${formatHM(it.diff_hours * 60)}`}
                                accent={it.diff_hours >= 0 ? "#376A4D" : "#9E4747"} />
                  </div>

                  {/* Expanded shifts list */}
                  {isOpen && (
                    <div className="border-t border-[#E5E1D8] bg-[#FBFAF6] px-3 sm:px-6 py-3">
                      {userShifts.length === 0 ? (
                        <div className="text-sm text-[#57534E] py-4 text-center">Načítám…</div>
                      ) : (
                        <ul className="space-y-2">
                          {userShifts.map((sh) => {
                            const isAuto = sh.status === "auto_closed";
                            return (
                            <li
                              key={sh.id}
                              data-testid={`att-shift-${sh.id}`}
                              data-auto-closed={isAuto ? "1" : "0"}
                              className={`bg-white border rounded-md p-3 flex items-start gap-3 flex-wrap sm:flex-nowrap ${
                                isAuto
                                  ? "border-[#C29329] border-l-4 border-l-[#C29329]"
                                  : "border-[#E5E1D8]"
                              }`}
                            >
                              {sh.start_selfie && (
                                <img src={sh.start_selfie} alt="" className="w-10 h-10 rounded object-cover border border-[#E5E1D8]" />
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="text-sm">
                                  <span className="font-medium">{formatDateTime(sh.start_at)}</span>
                                  <span className="text-[#57534E]"> → </span>
                                  <span>{sh.end_at ? formatDateTime(sh.end_at) : <span className="text-[#376A4D] font-medium">Probíhá</span>}</span>
                                  {isAuto && (
                                    <span
                                      data-testid="auto-closed-badge"
                                      className="ml-2 inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
                                      style={{ background: "#C293291F", color: "#8A6A1F", border: "1px solid #C2932966" }}
                                      title="Zaměstnanec nezapsal odchod. Systém směnu automaticky uzavřel 15 hodin po nástupu."
                                    >
                                      <AlertTriangle className="w-3 h-3" /> Auto-uzavřeno po 15 h
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-[#57534E] mt-0.5">
                                  {brName(sh.branch_id)}
                                  {sh.correction_note && <> · <em>{sh.correction_note}</em></>}
                                </div>
                              </div>
                              <div className="text-right shrink-0">
                                <div className="mono font-semibold">{formatHM(sh.total_minutes)}</div>
                                {sh.weekend_minutes > 0 && <div className="text-xs text-[#C29329]">vík. {formatHM(sh.weekend_minutes)}</div>}
                                {sh.holiday_minutes > 0 && <div className="text-xs text-[#9E4747]">svát. {formatHM(sh.holiday_minutes)}</div>}
                              </div>
                              <div className="flex gap-1.5 shrink-0">
                                <button
                                  data-testid={`att-edit-${sh.id}`}
                                  onClick={() => openEdit(sh)}
                                  className="p-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0]"
                                  title="Upravit"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                  data-testid={`att-delete-${sh.id}`}
                                  onClick={() => deleteShift(sh)}
                                  className="p-2 rounded-md border border-[#E5E1D8] text-[#9E4747] hover:bg-[#9E47471A]"
                                  title="Smazat"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </li>
                            );
                          })}
                        </ul>
                      )}
                      {/* Nepřítomnosti uživatele */}
                      {(() => {
                        const userAbs = absences.filter((a) => a.user_id === it.user_id);
                        if (userAbs.length === 0) return null;
                        return (
                          <div className="mt-3 pt-3 border-t border-[#E5E1D8]">
                            <div className="label-eyebrow mb-2 text-[#57534E]">Nepřítomnosti ({userAbs.length})</div>
                            <AbsenceList
                              items={userAbs}
                              onDelete={async (a) => {
                                try {
                                  await api.delete(`/attendance/absences/${a.id}`);
                                  toast.success("Záznam smazán");
                                  await load();
                                } catch (e) {
                                  toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
                                }
                              }}
                            />
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <AbsenceDialog
        open={absenceOpen}
        onClose={() => setAbsenceOpen(false)}
        onSaved={load}
        users={user.role === "admin" ? users : (summary?.items || []).map((it) => ({ id: it.user_id, name: it.user_name }))}
      />

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-md bg-white border-[#E5E1D8]" data-testid="att-edit-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>Upravit směnu</DialogTitle>
            <DialogDescription>
              Manuální korekce směny. Pro otevřenou směnu nechte „Konec" prázdný.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 pt-1">
              <label className="block">
                <div className="text-sm font-medium mb-1.5">Začátek</div>
                <input
                  data-testid="att-edit-start"
                  type="datetime-local"
                  className="input-cafe w-full"
                  value={editing.start}
                  onChange={(e) => setEditing({ ...editing, start: e.target.value })}
                />
              </label>
              <label className="block">
                <div className="text-sm font-medium mb-1.5">Konec</div>
                <input
                  data-testid="att-edit-end"
                  type="datetime-local"
                  className="input-cafe w-full"
                  value={editing.end}
                  onChange={(e) => setEditing({ ...editing, end: e.target.value })}
                />
              </label>
              <label className="block">
                <div className="text-sm font-medium mb-1.5">Poznámka ke korekci</div>
                <textarea
                  rows={2}
                  className="input-cafe w-full resize-none"
                  placeholder="např. zapomněl se odhlásit"
                  value={editing.note}
                  onChange={(e) => setEditing({ ...editing, note: e.target.value })}
                />
              </label>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0]">
              Zrušit
            </button>
            <button data-testid="att-edit-save" onClick={saveEdit} className="btn-primary inline-flex items-center gap-2">
              <Save className="w-4 h-4" /> Uložit
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Cell({ label, value, accent, strong, title }) {
  return (
    <div className="min-w-[80px]" title={title || undefined}>
      <div className="text-xs text-[#57534E] uppercase tracking-wider">{label}</div>
      <div className={"mono " + (strong ? "font-semibold" : "")} style={{ color: accent || "#1C1917" }}>
        {value}
      </div>
    </div>
  );
}

function MobileCell({ label, value, accent }) {
  return (
    <div className="border border-[#E5E1D8] rounded-md px-2 py-1.5 bg-white">
      <div className="text-[10px] text-[#57534E] uppercase">{label}</div>
      <div className="mono font-medium" style={{ color: accent || "#1C1917" }}>{value}</div>
    </div>
  );
}
